/**
 * ÚNICO lugar donde se lee la configuración de entorno (regla de arquitectura).
 * - API_BASE_URL (o VITE_API_BASE_URL) se inyecta en build por vite.config.ts → __API_BASE_URL__.
 * - Nadie más usa import.meta.env.* ni __API_BASE_URL__.
 */
const raw = import.meta.env;

const asNumber = (value: string | undefined, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const asOneOf = <T extends string>(
  value: string | undefined,
  allowed: readonly T[],
  fallback: T,
): T => ((allowed as readonly string[]).includes(value ?? '') ? (value as T) : fallback);

export const env = {
  /**
   * Base de la API sin barra final y sin /v1. Por defecto '/api' (mismo origen; Vite, Vercel, Netlify o
   * Nginx lo reenvían a la API real, sin CORS). Si API_BASE_URL es absoluta, se respeta.
   */
  apiBaseUrl: (__API_BASE_URL__ || '/api').replace(/\/+$/, ''),
  /** Versión de la API; las rutas de los servicios son relativas a esta. */
  apiVersion: '/v1',
  timeoutMs: asNumber(raw.VITE_TIMEOUT_MS, 15_000),
  defaultLang: asOneOf(raw.VITE_DEFAULT_LANG, ['es', 'en'] as const, 'es'),
  defaultTheme: asOneOf(raw.VITE_DEFAULT_THEME, ['light', 'dark', 'system'] as const, 'system'),
  demoMode: raw.VITE_DEMO_MODE === 'true',
  /** Pila de pruebas e2e (scripts/stack.sh, Playwright): sin publicidad ni otros extras. */
  e2e: raw.VITE_E2E === 'true',
  /** Publicidad (ver shared/components/ads): apagada salvo VITE_ADS_ENABLED=true. */
  adsEnabled: raw.VITE_ADS_ENABLED === 'true',
  adsClient: (raw.VITE_ADS_CLIENT ?? '').trim(),
  adsSlots: raw.VITE_ADS_SLOTS ?? '',
  siteUrl: (raw.VITE_SITE_URL ?? '').replace(/\/+$/, ''),
  appVersion: __APP_VERSION__,
  buildDate: __BUILD_DATE__,
  isDev: raw.DEV,
  isProd: raw.PROD,
} as const;

export type Env = typeof env;
